import type { MedplumClient } from "@medplum/core";
import type { Annotation, CodeableConcept, Coding, Condition, Extension } from "@medplum/fhirtypes";
import {
  type CreateResourceOptions,
  FhirResource,
  patchCodeableConcept,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef, ValueOf } from "@vibrant/models/fhir/fhir-resource-types";
import { createLogger } from "@/lib/logger";
import { pruneSystemValuePairs } from "@/lib/medplum/utils";
import { time } from "@/lib/time";
import { getVibrantSuperOrgId } from "@/lib/utils";
import { ConditionCodes } from "@/models/base/FhirValueset";
import { buildProvenanceSources } from "@/models/provenance";
import { searchResourcesByDocumentReference } from "@/models/source/document-source-lookup";
import { Source } from "@/models/source/Source";

export type ConditionRangeStatus = "out-of-range" | "not-optimal" | "monitor" | "unknown";
const logger = createLogger("MedicalCondition");

export interface CreateMedicalConditionRequest {
  patientId: string;
  description: string;
  codes?: Coding[];
  category?: string;
  date?: string;
  severity?: (typeof MedicalCondition.SeverityCode)[keyof typeof MedicalCondition.SeverityCode];
  status: (typeof MedicalCondition.Status)[keyof typeof MedicalCondition.Status];
  documentReferenceId?: string;
  surveyResponseId?: string;
  evidence?: ResourceRef[];
  notes?: string;
  /**
   * Stable identifiers used for idempotency.
   */
  identifier?: Array<{ system: string; value: string }>;
}

interface MedicalConditionCreateOptions {
  upsertQuery?: {
    patientId: string;
    code: string;
    status?: (typeof MedicalCondition.Status)[keyof typeof MedicalCondition.Status];
  };
  resourceOptions?: CreateResourceOptions;
}

interface AppendEvidenceParams {
  id: string;
  refs: ResourceRef[];
  options?: CreateResourceOptions;
}

/**
 * Wraps FHIR Condition resources for the member problem list and note-derived condition flows.
 */
export class MedicalCondition extends FhirResource<Condition> {
  static readonly ResourceType = "Condition";

  static readonly RangeStatusExtensionUrl = "vibrant.condition.range-status" as const;

  static readonly Status = {
    Active: "active",
    Recurrence: "recurrence",
    Relapse: "relapse",
    Inactive: "inactive",
    Remission: "remission",
    Resolved: "resolved",
    Unknown: "unknown",
  } as const;

  static readonly SeverityCode = {
    Mild: {
      display: "Mild",
      code: "255604002",
    },
    Moderate: {
      display: "Moderate",
      code: "6736007",
    },
    Severe: {
      display: "Severe",
      code: "24484000",
    },
  } as const;

  //------------------------------------------------------------------------------------------------
  //                                    VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------
  get description() {
    return this.resource.code?.text || this.resource.code?.coding?.[0]?.display;
  }

  get code() {
    return this.resource.code?.coding?.[0]?.code;
  }

  get status() {
    return this.resource.clinicalStatus?.coding?.[0]?.code;
  }

  get severity() {
    return this.resource.severity?.coding?.[0]?.display;
  }

  get onset() {
    return this.resource.onsetDateTime;
  }

  get endDate() {
    return this.resource.abatementDateTime;
  }

  get icd10Codes(): string[] {
    if (!this.resource.code?.coding) {
      return [];
    }

    // Both ICD-10 systems. CDA imports code conditions as plain ICD-10 while the document
    // parsers use ICD-10-CM, so filtering to CM alone made every imported diagnosis invisible
    // here — which is what stopped the parse-time matcher merging a parsed condition into the
    // imported one for the same code, and left the problem list showing no code for them.
    const codes = this.resource.code.coding
      .filter(
        (coding) =>
          coding.system === ConditionCodes.SYSTEM || coding.system === ConditionCodes.WHO_SYSTEM,
      )
      .map((coding) => coding.code)
      .filter((code): code is string => code !== undefined);

    // Deduped by value, because most conditions carry the SAME code under both systems and
    // callers render this with `join(", ")` — the problem list would read "(E66.3, E66.3)".
    // Codes that genuinely differ between the two systems (a CM code at finer granularity than
    // its WHO parent) are both kept, which is correct: they are different diagnoses.
    return codes.filter((code, index) => codes.indexOf(code) === index);
  }

  get notes(): Annotation[] {
    return this.resource.note || [];
  }

  get notesText(): string[] {
    return this.notes.map((note) => note.text || "").filter((text) => text !== "");
  }

  get updatedAt() {
    return this.resource.meta?.lastUpdated;
  }

  /**
   * Gets the range status from the extension, if present.
   * Validates the value against valid ConditionRangeStatus values before returning.
   */
  get rangeStatus(): ConditionRangeStatus | undefined {
    const extension = this.resource.extension?.find(
      (ext) => ext.url === MedicalCondition.RangeStatusExtensionUrl,
    );
    const value = extension?.valueString;

    if (
      value === "out-of-range" ||
      value === "not-optimal" ||
      value === "monitor" ||
      value === "unknown"
    ) {
      return value;
    }

    return undefined;
  }

  //------------------------------------------------------------------------------------------------
  //                                      STATIC METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Sets the condition status to resolved and records the resolution date.
   *
   * @param medplum - The Medplum client.
   * @param resourceId - The ID of the condition to resolve.
   * @returns The resolved condition.
   */
  static async resolve(medplum: MedplumClient, resourceId: string) {
    const resource = await MedicalCondition.patchResource(medplum, {
      id: resourceId,
      patch: {
        clinicalStatus: {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/condition-clinical",
              code: MedicalCondition.Status.Resolved,
            },
          ],
        },
        abatementDateTime: time().toISOString(),
      },
    });

    return new MedicalCondition(resource);
  }

  /**
   * Create a new medical condition.
   *
   * @param medplum - The Medplum client.
   * @param data.patientId - The ID of the patient.
   * @param data.description - The description of the condition.
   * @param data.code - The ICD-10 code of the condition.
   * @param data.date - The date of the condition.
   * @param data.status - The status of the condition.
   * @param data.severity - The severity of the condition.
   * @param data.notes - The notes for the condition.
   * @param options.upsertQuery - The query to upsert the condition.
   */
  static async create(
    medplum: MedplumClient,
    data: CreateMedicalConditionRequest,
    { upsertQuery, resourceOptions }: MedicalConditionCreateOptions = {},
  ) {
    const fhirObject = MedicalCondition.toFhirCreateObject(data);

    const resource = upsertQuery
      ? await MedicalCondition.upsertResource(medplum, {
          data: fhirObject,
          query: {
            code: upsertQuery.code,
            subject: `Patient/${upsertQuery.patientId}`,
            "clinical-status": upsertQuery.status,
          },
          options: resourceOptions,
        })
      : await MedicalCondition.createResource(medplum, fhirObject, resourceOptions);

    const medicalCondition = new MedicalCondition(resource);
    const sources = buildProvenanceSources({
      documentReferenceId: data.documentReferenceId,
      surveyResponseId: data.surveyResponseId,
    });

    if (medicalCondition.id && sources.length > 0) {
      await MedicalCondition.ensureProvenance(
        medplum,
        {
          medicalConditionId: medicalCondition.id,
          patientId: data.patientId,
          sources,
          occurredDate: data.date,
        },
        resourceOptions,
      );
    }

    return medicalCondition;
  }

  static toFhirCreateObject(data: CreateMedicalConditionRequest): ResourceData<Condition> {
    // Build evidence array including document reference if provided
    const evidence = [
      ...(data.evidence || []),
      ...(data.documentReferenceId
        ? [{ reference: `DocumentReference/${data.documentReferenceId}` }]
        : []),
      ...(data.surveyResponseId
        ? [{ reference: `QuestionnaireResponse/${data.surveyResponseId}` }]
        : []),
    ];

    return {
      subject: {
        reference: `Patient/${data.patientId}`,
      },
      identifier: pruneSystemValuePairs(data.identifier ?? []),
      evidence:
        evidence.length > 0
          ? evidence.map((ref) => ({
              detail: [{ reference: ref.reference }],
            }))
          : undefined,
      code: {
        text: data.description,
        coding: data.codes,
      },
      onsetDateTime: data.date,
      category: data.category ? [{ text: data.category }] : undefined,
      clinicalStatus: {
        coding: [
          { system: "http://terminology.hl7.org/CodeSystem/condition-clinical", code: data.status },
        ],
      },
      severity: data.severity ? { coding: [data.severity] } : undefined,
      note: data.notes ? [{ text: data.notes }] : undefined,
    };
  }

  /**
   * Search for medical conditions.
   *
   * @param medplum - The Medplum client.
   * @param query.status - The status of the conditions to search for.
   * @param query.patientRef - The reference to the patient to search for.
   * @returns An array of medical conditions.
   */
  static toFhirQuery(query: {
    status?: ValueOf<typeof MedicalCondition.Status> | string;
    name?: string;
    evidence?: ResourceRef[];
    patientRef: string;
    filter?: string;
  }): Record<string, any> {
    return {
      "clinical-status": query.status,
      subject: query.patientRef,
      "evidence-detail": query.evidence?.map((ref) => ref.reference).join(","),
      name: query.name,
      _filter: query.filter,
    };
  }

  static async search(
    medplum: MedplumClient,
    query: {
      status?: ValueOf<typeof MedicalCondition.Status> | string;
      name?: string;
      evidence?: ResourceRef[];
      patientRef: string;
      filter?: string;
    },
    options?: SearchOptions,
  ): Promise<MedicalCondition[]> {
    const resources = await MedicalCondition.searchResources(
      medplum,
      MedicalCondition.toFhirQuery(query),
      options,
    );

    return resources.map((resource) => new MedicalCondition(resource));
  }

  /**
   * Search for a medical condition by patient and code.
   *
   * @param medplum - The Medplum client.
   * @param query.patientRef - The reference to the patient to search for.
   * @param query.code - The code of the condition to search for.
   * @returns The medical condition.
   */
  static async searchOne(
    medplum: MedplumClient,
    query: {
      member: ResourceRef;
      codeLabel: string;
    },
  ): Promise<MedicalCondition | undefined> {
    const resource = await MedicalCondition.searchOneResource(medplum, {
      subject: query.member.reference,
      "code:text": query.codeLabel,
    });

    return resource ? new MedicalCondition(resource) : undefined;
  }

  /**
   * Subscribe to medical conditions for a patient.
   *
   * @param medplum - The Medplum client.
   * @param query.patientRef - The reference to the patient.
   * @param query.status - Optional status filter.
   * @param callback - A function to be called with each condition.
   */
  static subscribe(
    medplum: MedplumClient,
    query: {
      status?: ValueOf<typeof MedicalCondition.Status> | string;
      patientRef: string;
    },
    callback: SubscriptionCallback<MedicalCondition>,
  ) {
    return MedicalCondition.subscribeToResource(
      medplum,
      MedicalCondition.toFhirQuery(query),
      (resource, metadata) => callback(new MedicalCondition(resource), metadata),
    );
  }

  /**
   * Update a medical condition.
   *
   * @param medplum - The Medplum client.
   * @param resourceId - The ID of the condition to update.
   * @param data.severity - The severity of the condition.
   * @param data.status - The status of the condition.
   * @param data.evidence - The evidence of the condition.
   * @param data.notes - The notes to add to the condition.
   * @param data.rangeStatus - The range status.
   * @returns The updated condition.
   */
  static async update(
    medplum: MedplumClient,
    resourceId: string,
    data: {
      severity?: ValueOf<typeof MedicalCondition.SeverityCode>;
      status?: ValueOf<typeof MedicalCondition.Status>;
      evidence?: ResourceRef[];
      notes?: string;
      codes?: Coding[];
      description?: string;
      date?: string;
      endDate?: string;
      rangeStatus?: ConditionRangeStatus;
    },
  ) {
    // Read existing to safely merge CodeableConcept and extensions
    const existing = await MedicalCondition.searchOneResource(medplum, { _id: resourceId });
    if (!existing) throw new Error(`Condition/${resourceId} not found`);
    MedicalCondition.assertAbatementStatusInvariant({ existing, nextData: data });

    const mergedCode = MedicalCondition.mergeCodeableConcept({
      existingCode: existing.code,
      newCodes: data.codes,
      newText: data.description,
    });

    // Handle range status extension
    let extension: Extension[] | undefined;
    if (data.rangeStatus !== undefined) {
      extension = [
        {
          url: MedicalCondition.RangeStatusExtensionUrl,
          valueString: data.rangeStatus,
        } as Extension,
      ];
    }

    const resource = await MedicalCondition.patchResource(medplum, {
      id: resourceId,
      patch: {
        severity: data.severity ? { coding: [data.severity] } : undefined,
        clinicalStatus: data.status ? { coding: [{ code: data.status }] } : undefined,
        evidence: data.evidence?.map((ref) => ({
          detail: [{ reference: ref.reference }],
        })),
        code: mergedCode,
        onsetDateTime: data.date,
        abatementDateTime: data.endDate,
        note: data.notes?.trim()
          ? [{ text: data.notes.trim(), time: time().toISOString() }]
          : undefined,
        extension,
      },
    });

    return new MedicalCondition(resource);
  }

  /**
   * FHIR R4 con-4 allows abatement only on inactive, resolved, or remission conditions.
   */
  private static assertAbatementStatusInvariant({
    existing,
    nextData,
  }: {
    existing: Condition;
    nextData: { endDate?: string; status?: ValueOf<typeof MedicalCondition.Status> };
  }) {
    const nextEndDate =
      nextData.endDate ?? (nextData.status ? existing.abatementDateTime : undefined);
    if (nextEndDate === undefined) {
      return;
    }

    const nextStatus = nextData.status ?? existing.clinicalStatus?.coding?.[0]?.code;
    if (MedicalCondition.isAbatedClinicalStatus(nextStatus)) {
      return;
    }

    throw new Error(
      "Condition abatementDateTime requires clinicalStatus to be inactive, resolved, or remission",
    );
  }

  private static isAbatedClinicalStatus(status: string | undefined) {
    return (
      status === MedicalCondition.Status.Inactive ||
      status === MedicalCondition.Status.Resolved ||
      status === MedicalCondition.Status.Remission
    );
  }

  /**
   * Clears one of the persisted date fields with an explicit JSON Patch remove operation.
   */
  static async clearDate(
    medplum: MedplumClient,
    resourceId: string,
    field: "onsetDateTime" | "abatementDateTime",
  ) {
    const existing = await MedicalCondition.searchOneResource(medplum, { _id: resourceId });
    if (!existing) throw new Error(`Condition/${resourceId} not found`);

    if (existing[field] === undefined) {
      return new MedicalCondition(existing);
    }

    const resource = await MedicalCondition.patchResourceWithJsonPatch(medplum, resourceId, [
      {
        op: "remove",
        path: `/${field}`,
      },
    ]);

    return new MedicalCondition(resource);
  }

  /**
   * Merges `code` (CodeableConcept) updates safely with existing value.
   * - Preserves existing codings unless a new coding with same system is provided
   * - Adds new codings when provided
   * - Updates `text` when provided; removes it when provided as empty string
   * - Returns undefined if neither codes nor text are provided (no-op)
   */
  private static mergeCodeableConcept({
    existingCode,
    newCodes,
    newText,
  }: {
    existingCode: CodeableConcept | undefined;
    newCodes: Coding[] | undefined;
    newText: string | undefined;
  }): CodeableConcept | undefined {
    const hasCodes = Array.isArray(newCodes) && newCodes.length > 0;
    const hasTextInput = typeof newText === "string";
    if (!hasCodes && !hasTextInput) return undefined;

    // Merge codings by system; if no new codings provided, preserves existing as-is
    const mergedCodingConcept = patchCodeableConcept(existingCode, (newCodes ?? []) as any);

    // Normalize text input
    const normalizedText = hasTextInput ? newText?.trim() : undefined;

    // Apply text merge rules
    let result: CodeableConcept | undefined = mergedCodingConcept;
    if (hasTextInput) {
      const shouldRemoveText = normalizedText === "";
      if (!result) {
        if (shouldRemoveText) return undefined; // nothing to set
        result = { text: normalizedText } as CodeableConcept;
      } else {
        if (shouldRemoveText) delete (result as any).text;
        else (result as any).text = normalizedText;
      }
    }

    // If we ended up with neither coding nor text, return undefined to avoid wiping existing
    if (!result || (!result.text && (!result.coding || result.coding.length === 0))) {
      return undefined;
    }

    return result;
  }

  /**
   * Updates the notes for a medical condition.
   *
   * @param medplum - The Medplum client.
   * @param resourceId - The ID of the condition to update.
   * @param notes - The notes text to add/update.
   * @returns The updated condition.
   */
  static async updateNotes(medplum: MedplumClient, resourceId: string, notes: string) {
    const resource = await MedicalCondition.patchResource(medplum, {
      id: resourceId,
      patch: {
        note: notes.trim() ? [{ text: notes.trim(), time: time().toISOString() }] : [],
      },
    });

    return new MedicalCondition(resource);
  }

  /**
   * Appends evidence.detail references to a Condition, preserving existing evidence and codes.
   * - Merges with existing evidence.detail[].reference values
   * - Deduplicates by reference string
   * - Does not modify existing evidence.code entries
   */
  static async appendEvidence(
    medplum: MedplumClient,
    { id, refs, options }: AppendEvidenceParams,
  ): Promise<MedicalCondition> {
    const newRefStrings = (refs ?? [])
      .map((r) => r.reference?.trim())
      .filter((r): r is string => Boolean(r));

    // Short-circuit if nothing to add
    const existingResource = await MedicalCondition.searchOneResource(medplum, { _id: id });
    if (!existingResource) throw new Error(`Condition/${id} not found`);
    if (newRefStrings.length === 0) return new MedicalCondition(existingResource);

    const existingEvidence = existingResource.evidence ?? [];
    const existingRefSet = new Set<string>();
    for (const ev of existingEvidence) {
      for (const det of ev.detail ?? []) {
        if (det.reference) existingRefSet.add(det.reference);
      }
    }

    const missingRefStrings = newRefStrings.filter((ref) => !existingRefSet.has(ref));
    if (missingRefStrings.length === 0) return new MedicalCondition(existingResource);

    const appendedEvidenceEntries = missingRefStrings.map((reference) => ({
      detail: [{ reference }],
    }));

    const combinedEvidence = [...existingEvidence, ...appendedEvidenceEntries];

    const resource = await MedicalCondition.patchResource(medplum, {
      id,
      patch: {
        evidence: combinedEvidence,
      },
    });

    const patientReference = existingResource.subject?.reference;
    const patientId = patientReference?.startsWith("Patient/")
      ? patientReference.split("/")[1]
      : undefined;

    if (patientId) {
      await MedicalCondition.ensureProvenance(
        medplum,
        {
          medicalConditionId: id,
          patientId,
          sources: missingRefStrings.map((reference) => ({ reference })),
        },
        options,
      );
    }

    return new MedicalCondition(resource);
  }

  /**
   * Ensures a Provenance resource exists for this medical condition, idempotently adding sources.
   *
   * @param medplum - The Medplum client.
   * @param args - Provenance configuration.
   */
  static async ensureProvenance(
    medplum: MedplumClient,
    args: {
      medicalConditionId: string;
      patientId: string;
      sources: { reference: string }[];
      activity?: CodeableConcept;
      occurredDate?: string;
    },
    options?: CreateResourceOptions,
  ): Promise<void> {
    const { medicalConditionId, patientId, sources, activity, occurredDate } = args;

    if (sources.length === 0) {
      logger.warn("No sources provided for Provenance", { medicalConditionId });
      return;
    }

    try {
      const hasSurveyResponse = sources.some((source) =>
        source.reference?.startsWith("QuestionnaireResponse/"),
      );

      await Source.create(
        medplum,
        {
          for: { reference: `${MedicalCondition.ResourceType}/${medicalConditionId}` },
          date: time().toISOString(),
          author: hasSurveyResponse ? { reference: `Patient/${patientId}` } : undefined,
          assembler: hasSurveyResponse
            ? undefined
            : { reference: `Organization/${getVibrantSuperOrgId()}` },
          sources,
          activity,
          occurredDate,
        },
        options,
      );
    } catch (error) {
      logger.error("Error ensuring Provenance for medical condition", {
        medicalConditionId,
        error,
      });
      // Don't throw - provenance is additive metadata.
    }
  }

  /**
   * Searches for medical conditions by document reference ID.
   *
   * @param medplum - The medplum client.
   * @param documentReferenceId - The document reference ID.
   * @returns The medical conditions that have evidence from the specified document.
   */
  static async searchByDocumentReferenceId(medplum: MedplumClient, documentReferenceId: string) {
    const resources = await searchResourcesByDocumentReference<Condition>({
      medplum,
      model: MedicalCondition,
      documentReferenceId,
      legacyQuery: {
        "evidence-detail": `DocumentReference/${documentReferenceId}`,
      },
    });

    return resources.map((resource) => new MedicalCondition(resource));
  }
}
